import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../utils/api";
import { fetchTeachers } from "./dataService";
import "./TimetableCampusPage.css";

const PERIODS_KEY = "als_timetable_periods";
const DAYS_KEY = "als_timetable_days";

// Default 8 teaching periods with a break after Period 5.
function defaultPeriods() {
  return [
    { id: "p1", type: "period", label: "Period 1", start_time: "08:00", end_time: "08:45" },
    { id: "p2", type: "period", label: "Period 2", start_time: "08:45", end_time: "09:30" },
    { id: "p3", type: "period", label: "Period 3", start_time: "09:30", end_time: "10:15" },
    { id: "p4", type: "period", label: "Period 4", start_time: "10:15", end_time: "11:00" },
    { id: "p5", type: "period", label: "Period 5", start_time: "11:00", end_time: "11:45" },
    { id: "break", type: "break", label: "Break", start_time: "11:45", end_time: "12:00" },
    { id: "p6", type: "period", label: "Period 6", start_time: "12:00", end_time: "12:45" },
    { id: "p7", type: "period", label: "Period 7", start_time: "12:45", end_time: "13:30" },
    { id: "p8", type: "period", label: "Period 8", start_time: "13:30", end_time: "14:15" },
  ];
}

// Default 6 days (Monday–Saturday). User can edit this later.
function defaultDays() {
  return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
}

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (err) {
    console.error(`Failed to parse localStorage key "${key}"`, err);
    return fallback;
  }
}

function saveToStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`Failed to write localStorage key "${key}"`, err);
  }
}

function toMinutes(t) {
  if (!t || typeof t !== "string" || !t.includes(":")) return NaN;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function timesOverlap(aStart, aEnd, bStart, bEnd) {
  return toMinutes(aStart) < toMinutes(bEnd) && toMinutes(bStart) < toMinutes(aEnd);
}

function formatTime12(t) {
  if (!t || !t.includes(":")) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return "";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

function formatRange(period) {
  if (!period) return "";
  return `${formatTime12(period.start_time)} – ${formatTime12(period.end_time)}`;
}

const emptyModalForm = { course_id: "", teacher_id: "" };

function TimetableCampusPage({ onLogout, showToast }) {
  const { campusId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("als_token");

  const [campus, setCampus] = useState(null);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [coursesByClass, setCoursesByClass] = useState({});
  const [timetable, setTimetable] = useState([]); // entries for THIS campus only
  const [periods, setPeriods] = useState(defaultPeriods());
  const [days, setDays] = useState(defaultDays());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [view, setView] = useState("grid"); // "grid" | "teacher"
  const [showPeriodEditor, setShowPeriodEditor] = useState(false);
  const [showDaysEditor, setShowDaysEditor] = useState(false);

  const [selectedClassName, setSelectedClassName] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const [modal, setModal] = useState(null); // { day, period_id, entry }
  const [modalForm, setModalForm] = useState(emptyModalForm);

  // Load campus, classes, teachers, course links, and timetable entries from backend API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [campusRes, classesRes, teachersData, courseCampusRes, timetableRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/campuses/${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/classes?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetchTeachers(),
          fetch(`${API_BASE_URL}/api/course-campus?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/timetable?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if ([campusRes, classesRes, courseCampusRes, timetableRes].some((r) => r.status === 401)) {
          onLogout && onLogout();
          return;
        }

        setCampus(campusRes.ok ? await campusRes.json() : null);
        setClasses(classesRes.ok ? await classesRes.json() : []);

        if (Array.isArray(teachersData)) {
          const campusTeachers = teachersData.filter(
            (t) => String(t.campus_id) === String(campusId),
          );
          const seen = new Set();
          const deduped = campusTeachers.filter((t) => {
            const key = t.cnic
              ? `cnic:${String(t.cnic).trim()}`
              : `nc:${String(t.name).trim()}|${String(t.contact_number).trim()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setTeachers(deduped);
        }

        if (courseCampusRes.ok) {
          const ccData = await courseCampusRes.json();
          const grouped = {};
          if (Array.isArray(ccData)) {
            ccData.forEach((link) => {
              const classIdKey = String(link.class_id);
              if (!grouped[classIdKey]) {
                grouped[classIdKey] = [];
              }
              grouped[classIdKey].push({
                id: link.course_id,
                name: link.course_name || link.name,
              });
            });
          }
          setCoursesByClass(grouped);
        }

        if (timetableRes.ok) {
          const ttData = await timetableRes.json();
          if (Array.isArray(ttData)) {
            const mapped = ttData.map((entry) => ({
              id: entry.id,
              class_id: entry.class_id,
              course_id: entry.course_id,
              course_name: entry.course_name,
              teacher_id: entry.teacher_id,
              teacher_name: entry.teacher_name,
              day: entry.day_of_week,
              period_id: `p${entry.period_number}`,
            }));
            setTimetable(mapped);
          }
        }
      } catch (err) {
        console.error("Failed to load campus/class/timetable data", err);
        showToast && showToast("error", "Load Failed", "Could not load data for this campus.");
      } finally {
        setLoading(false);
      }
    };

    if (campusId && token) {
      fetchData();
    }
  }, [campusId, token, onLogout, showToast]);

  // Saved period times and days from localStorage
  useEffect(() => {
    if (loading) return;

    const allPeriods = loadFromStorage(PERIODS_KEY, {});
    setPeriods(Array.isArray(allPeriods[String(campusId)]) ? allPeriods[String(campusId)] : defaultPeriods());

    const allDays = loadFromStorage(DAYS_KEY, {});
    setDays(Array.isArray(allDays[String(campusId)]) ? allDays[String(campusId)] : defaultDays());
  }, [campusId, loading]);

  // Default the Class / Section pickers once classes have loaded.
  useEffect(() => {
    if (classes.length === 0) return;
    if (!selectedClassName) {
      setSelectedClassName(classes[0].name);
    }
  }, [classes, selectedClassName]);

  const classNames = useMemo(() => {
    const names = [];
    classes.forEach((c) => {
      if (!names.includes(c.name)) names.push(c.name);
    });
    return names;
  }, [classes]);

  const sectionsForSelectedClassName = useMemo(
    () => classes.filter((c) => c.name === selectedClassName),
    [classes, selectedClassName],
  );

  useEffect(() => {
    if (sectionsForSelectedClassName.length === 0) {
      setSelectedSection("");
      return;
    }
    const stillValid = sectionsForSelectedClassName.some(
      (c) => (c.section || "—") === selectedSection,
    );
    if (!stillValid) {
      setSelectedSection(sectionsForSelectedClassName[0].section || "—");
    }
  }, [sectionsForSelectedClassName, selectedSection]);

  const selectedClass = useMemo(
    () =>
      sectionsForSelectedClassName.find((c) => (c.section || "—") === selectedSection) || null,
    [sectionsForSelectedClassName, selectedSection],
  );
  const selectedClassId = selectedClass ? selectedClass.id : null;

  const persistPeriods = (next) => {
    const all = loadFromStorage(PERIODS_KEY, {});
    all[String(campusId)] = next;
    saveToStorage(PERIODS_KEY, all);
    setPeriods(next);
  };

  const persistDays = (next) => {
    const all = loadFromStorage(DAYS_KEY, {});
    all[String(campusId)] = next;
    saveToStorage(DAYS_KEY, all);
    setDays(next);
  };

  const updatePeriodField = (periodId, field, value) => {
    setPeriods((prev) => {
      const next = prev.map((p) => (p.id === periodId ? { ...p, [field]: value } : p));
      persistPeriods(next);
      return next;
    });
  };

  const addPeriod = () => {
    setPeriods((prev) => {
      const lastPeriod = prev.filter((p) => p.type === "period").slice(-1)[0];
      const nextId = `p${prev.filter((p) => p.type === "period").length + 1}`;
      const newLabel = `Period ${prev.filter((p) => p.type === "period").length + 1}`;
      const start = lastPeriod ? lastPeriod.end_time : "14:15";
      // Simple default: 45-minute period
      const [sh, sm] = start.split(":").map(Number);
      let endMinutes = sh * 60 + sm + 45;
      const endH = String(Math.floor(endMinutes / 60)).padStart(2, "0");
      const endM = String(endMinutes % 60).padStart(2, "0");
      const end = `${endH}:${endM}`;

      const newPeriod = {
        id: nextId,
        type: "period",
        label: newLabel,
        start_time: start,
        end_time: end,
      };
      const next = [...prev, newPeriod];
      persistPeriods(next);
      return next;
    });
  };

  const removePeriod = (periodId) => {
    setPeriods((prev) => {
      const filtered = prev.filter((p) => p.id !== periodId);
      persistPeriods(filtered);
      return filtered;
    });
  };

  const addDay = () => {
    setDays((prev) => {
      const base = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
      const existingLower = prev.map((d) => d.toLowerCase());
      const nextName = base.find((d) => !existingLower.includes(d.toLowerCase())) || "Custom Day";
      const next = [...prev, nextName];
      persistDays(next);
      return next;
    });
  };

  const removeDay = (index) => {
    setDays((prev) => {
      const next = prev.filter((_, i) => i !== index);
      persistDays(next);
      return next;
    });
  };

  const updateDayLabel = (index, value) => {
    setDays((prev) => {
      const next = prev.map((d, i) => (i === index ? value : d));
      persistDays(next);
      return next;
    });
  };

  const getPeriod = (periodId) => periods.find((p) => p.id === periodId);

  const getClassCourses = (classId) => coursesByClass[String(classId)] || [];

  const getClassLabel = (classId) => {
    const cls = classes.find((c) => String(c.id) === String(classId));
    return cls ? `${cls.name}${cls.section ? " — " + cls.section : ""}` : "Unknown Class";
  };

  const eligibleTeachersForCourse = (courseId) => {
    if (!courseId) return [];
    const assigned = teachers.filter(
      (t) =>
        Array.isArray(t.assigned_courses) &&
        t.assigned_courses.some((c) => String(c.course_id) === String(courseId)),
    );
    return assigned.length > 0 ? assigned : teachers;
  };

  const courseHasAssignedTeacher = (courseId) =>
    teachers.some(
      (t) =>
        Array.isArray(t.assigned_courses) &&
        t.assigned_courses.some((c) => String(c.course_id) === String(courseId)),
    );

  const findConflict = ({ day, period_id, teacher_id, class_id, excludeId }) => {
    const currentPeriod = getPeriod(period_id);
    if (!currentPeriod) return null;

    for (const entry of timetable) {
      if (excludeId && String(entry.id) === String(excludeId)) continue;
      if (entry.day !== day) continue;

      const entryPeriod = getPeriod(entry.period_id);
      if (!entryPeriod) continue;
      if (
        !timesOverlap(
          currentPeriod.start_time,
          currentPeriod.end_time,
          entryPeriod.start_time,
          entryPeriod.end_time,
        )
      ) {
        continue;
      }

      if (String(entry.teacher_id) === String(teacher_id)) {
        return {
          type: "teacher",
          message: `${entry.teacher_name} is already teaching ${entry.course_name} in ${getClassLabel(
            entry.class_id,
          )} on ${entry.day} during ${formatRange(entryPeriod)}.`,
        };
      }
      if (String(entry.class_id) === String(class_id)) {
        return {
          type: "class",
          message: `${getClassLabel(class_id)} already has ${entry.course_name} scheduled on ${entry.day} during ${formatRange(entryPeriod)}.`,
        };
      }
    }
    return null;
  };

  const getEntry = (day, periodId) =>
    timetable.find(
      (e) =>
        String(e.class_id) === String(selectedClassId) &&
        e.day === day &&
        e.period_id === periodId,
    );

  const openAddModal = (day, period) => {
    if (!selectedClassId) return;
    setModal({ day, period_id: period.id, entry: null });
    setModalForm(emptyModalForm);
  };

  const openEditModal = (entry) => {
    setModal({ day: entry.day, period_id: entry.period_id, entry });
    setModalForm({ course_id: String(entry.course_id), teacher_id: String(entry.teacher_id) });
  };

  const closeModal = () => {
    setModal(null);
    setModalForm(emptyModalForm);
  };

  const handleModalFormChange = (field, value) => {
    setModalForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "course_id") next.teacher_id = "";
      return next;
    });
  };

  const handleModalSave = async (e) => {
    e.preventDefault();
    if (!modal || !selectedClassId) return;

    const { course_id, teacher_id } = modalForm;
    if (!course_id || !teacher_id) {
      showToast && showToast("error", "Missing Info", "Please select both a course and a teacher.");
      return;
    }

    const conflict = findConflict({
      day: modal.day,
      period_id: modal.period_id,
      teacher_id,
      class_id: selectedClassId,
      excludeId: modal.entry ? modal.entry.id : null,
    });
    if (conflict) {
      showToast && showToast(
        "error",
        conflict.type === "teacher" ? "Teacher Double-Booked" : "Class Already Scheduled",
        conflict.message,
      );
      return;
    }

    const course = getClassCourses(selectedClassId).find((c) => String(c.id) === String(course_id));
    const teacher = teachers.find((t) => String(t.id) === String(teacher_id));

    const periodNum = parseInt(modal.period_id.replace("p", ""), 10);
    const periodObj = getPeriod(modal.period_id);

    const payload = {
      campus_id: Number(campusId),
      class_id: selectedClassId,
      section: selectedClass?.section ?? null,
      day_of_week: modal.day,
      period_number: periodNum,
      start_time: periodObj ? periodObj.start_time : null,
      end_time: periodObj ? periodObj.end_time : null,
      course_id: Number(course_id),
      teacher_id: Number(teacher_id),
      room: null,
    };

    setSubmitting(true);
    try {
      let savedEntry;
      if (modal.entry) {
        // Update
        const response = await fetch(`${API_BASE_URL}/api/timetable/${modal.entry.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Failed to update timetable entry");
        savedEntry = await response.json();

        const mappedEntry = {
          id: savedEntry.id,
          class_id: savedEntry.class_id,
          course_id: savedEntry.course_id,
          course_name: savedEntry.course_name || course?.name || "Unknown Course",
          teacher_id: savedEntry.teacher_id,
          teacher_name: savedEntry.teacher_name || teacher?.name || "Unknown Teacher",
          day: savedEntry.day_of_week,
          period_id: `p${savedEntry.period_number}`,
        };

        setTimetable((prev) =>
          prev.map((en) => (String(en.id) === String(modal.entry.id) ? mappedEntry : en))
        );
        showToast && showToast("success", "Period Updated", "The timetable slot has been updated.");
      } else {
        // Create
        const response = await fetch(`${API_BASE_URL}/api/timetable`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Failed to create timetable entry");
        savedEntry = await response.json();

        const mappedEntry = {
          id: savedEntry.id,
          class_id: savedEntry.class_id,
          course_id: savedEntry.course_id,
          course_name: savedEntry.course_name || course?.name || "Unknown Course",
          teacher_id: savedEntry.teacher_id,
          teacher_name: savedEntry.teacher_name || teacher?.name || "Unknown Teacher",
          day: savedEntry.day_of_week,
          period_id: `p${savedEntry.period_number}`,
        };

        setTimetable((prev) => [...prev, mappedEntry]);
        showToast && showToast("success", "Period Added", `${mappedEntry.course_name} added on ${modal.day}.`);
      }
      closeModal();
    } catch (err) {
      console.error(err);
      showToast && showToast("error", "Save Failed", "Could not save timetable entry to backend.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalDelete = async () => {
    if (!modal || !modal.entry) return;
    if (!window.confirm(`Remove "${modal.entry.course_name}" from this slot?`)) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/timetable/${modal.entry.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to delete timetable entry");

      setTimetable((prev) => prev.filter((en) => String(en.id) !== String(modal.entry.id)));
      closeModal();
      showToast && showToast("success", "Period Removed", "The timetable slot has been cleared.");
    } catch (err) {
      console.error(err);
      showToast && showToast("error", "Remove Failed", "Could not remove timetable entry.");
    } finally {
      setSubmitting(false);
    }
  };


  const slotsThisWeek = selectedClassId
    ? timetable.filter((e) => String(e.class_id) === String(selectedClassId)).length
    : 0;

  const teacherGroups = teachers.map((t) => {
    const entries = timetable
      .filter((e) => String(e.teacher_id) === String(t.id))
      .map((e) => ({ ...e, period: getPeriod(e.period_id), class_label: getClassLabel(e.class_id) }))
      .filter((e) => e.period)
      .sort((a, b) => {
        const dayDiff = days.indexOf(a.day) - days.indexOf(b.day);
        if (dayDiff !== 0) return dayDiff;
        return toMinutes(a.period.start_time) - toMinutes(b.period.start_time);
      });
    return { teacher: t, entries };
  });

  if (loading) {
    return <div className="page-loading">Loading...</div>;
  }

  return (
    <div className="tt-page">
      <div className="tt-header">
        <button className="tt-back" onClick={() => navigate("/teachers/timetable")}>
          ← Back
        </button>
        <h2>Timetable{campus ? ` — ${campus.name}` : ""}</h2>

        <div className="tt-header-actions">
          <button
            className={`tt-view-toggle ${view === "grid" ? "active" : ""}`}
            onClick={() => setView("grid")}
          >
            Grid
          </button>
          <button
            className={`tt-view-toggle ${view === "teacher" ? "active" : ""}`}
            onClick={() => setView("teacher")}
          >
            By Teacher
          </button>
          {view === "grid" && (
            <>
              <button className="tt-period-edit-btn" onClick={() => setShowPeriodEditor((v) => !v)}>
                {showPeriodEditor ? "Close" : "Edit Periods & Times"}
              </button>
              <button className="tt-period-edit-btn" onClick={() => setShowDaysEditor((v) => !v)}>
                {showDaysEditor ? "Close" : "Edit Days"}
              </button>
            </>
          )}
        </div>
      </div>

      {view === "grid" && (
        <>
          {showPeriodEditor && (
            <div className="tt-period-editor">
              <div className="tt-period-editor-title">Period Times</div>
              <div className="tt-period-editor-grid">
                {periods.map((p) => (
                  <div key={p.id} className={`tt-period-editor-row ${p.type === "break" ? "is-break" : ""}`}>
                    <input
                      className="tt-period-editor-label"
                      value={p.label}
                      onChange={(e) => updatePeriodField(p.id, "label", e.target.value)}
                    />
                    <input
                      type="time"
                      value={p.start_time}
                      onChange={(e) => updatePeriodField(p.id, "start_time", e.target.value)}
                    />
                    <span className="tt-period-editor-sep">to</span>
                    <input
                      type="time"
                      value={p.end_time}
                      onChange={(e) => updatePeriodField(p.id, "end_time", e.target.value)}
                    />
                    {p.type === "break" && <span className="tt-break-tag">Break</span>}
                    {p.type !== "break" && (
                      <button
                        type="button"
                        className="tt-period-remove-btn"
                        onClick={() => removePeriod(p.id)}
                        title="Remove this period"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="tt-add-period-btn" onClick={addPeriod}>
                  + Add Period
                </button>
              </div>
            </div>
          )}

          {showDaysEditor && (
            <div className="tt-period-editor">
              <div className="tt-period-editor-title">Days of the Week</div>
              <div className="tt-days-editor-grid">
                {days.map((d, idx) => (
                  <div key={`${d}-${idx}`} className="tt-day-editor-row">
                    <input
                      className="tt-day-editor-label"
                      value={d}
                      onChange={(e) => updateDayLabel(idx, e.target.value)}
                    />
                    <button
                      type="button"
                      className="tt-day-remove-btn"
                      onClick={() => removeDay(idx)}
                      title="Remove this day"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className="tt-add-day-btn" onClick={addDay}>
                  + Add Day
                </button>
              </div>
            </div>
          )}

          <div className="tt-controls-bar">
            <div className="tt-control-field">
              <label>Class</label>
              <select value={selectedClassName} onChange={(e) => setSelectedClassName(e.target.value)}>
                {classNames.length === 0 && <option value="">No classes</option>}
                {classNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="tt-control-field">
              <label>Section</label>
              <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}>
                {sectionsForSelectedClassName.length === 0 && <option value="">—</option>}
                {sectionsForSelectedClassName.map((c) => (
                  <option key={c.id} value={c.section || "—"}>
                    {c.section || "—"}
                  </option>
                ))}
              </select>
            </div>

            <div className="tt-slots-count">
              <span className="tt-slots-icon">🗓</span>
              {slotsThisWeek} slot{slotsThisWeek !== 1 ? "s" : ""} scheduled this week
            </div>
          </div>

          {classes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-text">No classes found for this campus.</div>
            </div>
          ) : !selectedClassId ? (
            <div className="empty-state">
              <div className="empty-text">Select a class and section to view its timetable.</div>
            </div>
          ) : getClassCourses(selectedClassId).length === 0 ? (
            <div className="empty-state">
              <div className="empty-text">
                No courses have been added to this class yet. Add a course first from "Add Course".
              </div>
            </div>
          ) : (
            <div className="tt-grid-wrapper">
              <table className="tt-grid">
                <thead>
                  <tr>
                    <th className="tt-col-period">PERIOD</th>
                    {days.map((d) => (
                      <th key={d}>{d.slice(0, 3).toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period, rowIdx) =>
                    period.type === "break" ? (
                      <tr key={period.id} className="tt-break-row">
                        <td className="tt-period-label">
                          <div className="tt-period-name">{period.label}</div>
                          <div className="tt-period-time">{formatRange(period)}</div>
                        </td>
                        <td colSpan={days.length} className="tt-break-cell">
                          <span className="tt-break-pill">☕ Break · {formatRange(period)}</span>
                        </td>
                      </tr>
                    ) : (
                      <tr key={period.id} className={rowIdx % 2 === 0 ? "tt-row-a" : "tt-row-b"}>
                        <td className="tt-period-label">
                          <div className="tt-period-name">{period.label}</div>
                          <div className="tt-period-time">{formatRange(period)}</div>
                        </td>
                        {days.map((day) => {
                          const entry = getEntry(day, period.id);
                          return (
                            <td key={day} className="tt-cell" data-day={day.slice(0, 3).toUpperCase()}>
                              {entry ? (
                                <div className="tt-slot-filled" onClick={() => openEditModal(entry)}>
                                  <div className="tt-slot-course">{entry.course_name}</div>
                                  <div className="tt-slot-teacher">{entry.teacher_name}</div>
                                </div>
                              ) : (
                                <button className="tt-slot-add" onClick={() => openAddModal(day, period)}>
                                  <span className="tt-plus">+</span> Add
                                </button>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === "teacher" && (
        <div className="tt-teacher-view">
          {teachers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-text">No teachers registered for this campus yet.</div>
            </div>
          ) : (
            teacherGroups.map(({ teacher, entries }) => (
              <div key={teacher.id} className="tt-teacher-card">
                <div className="tt-teacher-card-header">
                  <span className="tt-teacher-name">{teacher.name}</span>
                  <span className="tt-slot-badge">
                    {entries.length} period{entries.length !== 1 ? "s" : ""}
                  </span>
                </div>
                {entries.length === 0 ? (
                  <div className="no-assigned-message">No periods scheduled.</div>
                ) : (
                  <table className="tt-teacher-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left" }}>Day</th>
                        <th style={{ textAlign: "left" }}>Time</th>
                        <th style={{ textAlign: "left" }}>Class</th>
                        <th style={{ textAlign: "left" }}>Course</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr key={entry.id}>
                          <td style={{ textAlign: "left" }}>{entry.day}</td>
                          <td style={{ textAlign: "left" }}>{formatRange(entry.period)}</td>
                          <td style={{ textAlign: "left" }}>{entry.class_label}</td>
                          <td style={{ textAlign: "left" }}>{entry.course_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {modal && (
        <div className="tt-modal-overlay" onClick={closeModal}>
          <div className="tt-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{modal.entry ? "Edit Period" : "Add Period"}</h3>
            <p className="tt-modal-meta">
              {getClassLabel(selectedClassId)} · {modal.day} · {formatRange(getPeriod(modal.period_id))}
            </p>

            <form onSubmit={handleModalSave}>
              <div className="tt-modal-field">
                <label htmlFor="tt-modal-course">Course</label>
                <select
                  id="tt-modal-course"
                  value={modalForm.course_id}
                  onChange={(e) => handleModalFormChange("course_id", e.target.value)}
                >
                  <option value="">Select course</option>
                  {getClassCourses(selectedClassId).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="tt-modal-field">
                <label htmlFor="tt-modal-teacher">Teacher</label>
                <select
                  id="tt-modal-teacher"
                  value={modalForm.teacher_id}
                  onChange={(e) => handleModalFormChange("teacher_id", e.target.value)}
                  disabled={!modalForm.course_id}
                >
                  <option value="">Select teacher</option>
                  {eligibleTeachersForCourse(modalForm.course_id).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {modalForm.course_id && !courseHasAssignedTeacher(modalForm.course_id) && (
                  <span className="field-hint">
                    No teacher is assigned to this course yet — showing all campus teachers.
                  </span>
                )}
              </div>

              <div className="tt-modal-actions">
                {modal.entry && (
                  <button type="button" className="tt-modal-delete" onClick={handleModalDelete}>
                    Delete
                  </button>
                )}
                <div className="tt-modal-actions-right">
                  <button type="button" className="tt-modal-cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="tt-modal-save" disabled={submitting}>
                    {submitting ? "Saving..." : modal.entry ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimetableCampusPage;